import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Chip,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as ChatTag } from "../../../../assets/images/ChatImages/chattag.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/images/TicketImages/search.svg";
import { fetchTags } from "../../../../store/features/tagsApi";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { ReactComponent as Cancel } from "../../../../assets/images/TicketImages/cancel.svg";
import "./style.css";
import { toast } from "react-toastify";
import NoSearch from "../../../common/NoSearch";
import DropdownSelectionTick from "../../People/DropdownSelectionTick";

function TicketTagInput({
  tags,
  setTags,
  formControlSx = {},
  showHeader = true,
  chipsContainerSx = {},
  headerLabel = "Tags",
  returnIds = false,
  dropDownTop = 'calc(100% + 8px)',
  sxHeight = 40,
  allowHeightExpansion = false, // New prop to control behavior
  dropdownWidth,
  dropdownMatchTrigger = false,
  dropdownHeight = 232,
  showSelectionTick = false,
  emptyLabel = "Add Tags",
  singleLineEllipsis = false,
  showTagsPrefix = true,
  centerContent = false,
  hideInlineChips = false,
  maxTags,
  uiMode,
}) {
  const labelEllipsisStyle = singleLineEllipsis
    ? {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
        minWidth: 0,
      }
    : { flex: 1 };
  const dispatch = useDispatch();
  const businessId = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const { list: availableTags = [], loading } = useSelector((state) => state.tags);
  const [isWrapped, setIsWrapped] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const selectInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const addButtonOnlyTriggerRef = useRef(null);
  const [addButtonDropdownFixed, setAddButtonDropdownFixed] = useState(null);

  useEffect(() => {
    if (businessId) {
      dispatch(fetchTags({ businessId, type: "conversation" }));
    }
  }, [dispatch, businessId]);
  useEffect(() => {
    if (centerContent || !selectInputRef.current) return;
    const el = selectInputRef.current;
    setIsWrapped(el.scrollHeight > el.clientHeight);
  }, [tags, centerContent]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Normalize availableTags to ensure consistent data
  const normalizedTags = (availableTags || []).map((tag) => ({
    _id: tag._id || tag.name || String(tag),
    name: tag.name || String(tag),
  }));

  // Filter tags based on search term
  const filteredTags = normalizedTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const maxAllowed =
    typeof maxTags === "number" && maxTags > 0 && maxTags <= 50 ? maxTags : 5;

  const handleTagSelect = (selectedTag) => {
    const tagValue = returnIds ? selectedTag._id : selectedTag.name;

    if (tags.includes(tagValue)) {
      return; // Tag already selected
    }

    if (tags.length >= maxAllowed) {
      toast.error(`You can only add up to ${maxAllowed} tags.`);
      return;
    }

    setTags([...tags, tagValue]);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleDeleteTag = (tagToDelete) => {
    setTags((prev) =>
      prev.filter((tagId) => tagId !== tagToDelete)
    );
  };

  const handleInputClick = () => {
    setIsOpen(!isOpen);
    setSearchTerm("");
  };

  useEffect(() => {
    if (tags.length > 0 && selectInputRef.current) {
      selectInputRef.current.scrollLeft = selectInputRef.current.scrollWidth;
    }
  }, [tags]);

  useLayoutEffect(() => {
    if (uiMode !== "addButtonOnly" || !isOpen) {
      setAddButtonDropdownFixed(null);
      return undefined;
    }
    const el = addButtonOnlyTriggerRef.current;
    if (!el) return undefined;

    const updatePosition = () => {
      const rect = el.getBoundingClientRect();
      const panelW = 248;
      const margin = 8;
      let left = rect.left;
      if (left + panelW > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - margin - panelW);
      }
      if (left < margin) left = margin;
      setAddButtonDropdownFixed({
        position: "fixed",
        top: rect.bottom + 8,
        left,
        width: panelW,
        zIndex: 1350,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [uiMode, isOpen]);

  if (uiMode === "addButtonOnly") {
    const panelChrome = {
      border: "1px solid #E5E7EB",
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1)",
      display: "flex",
      padding: "8px 8px",
      flexDirection: "column",
      maxHeight: "232px",
      overflow: "hidden",
    };

    return (
      <div
        className="TicketTagInputContainer-Main"
        style={{ display: "contents" }}
      >
        <div
          ref={containerRef}
          style={{
            position: "relative",
            display: "inline-flex",
            flexShrink: 0,
            alignItems: "center",
            alignSelf: "center",
          }}
        >
          <Box
            ref={addButtonOnlyTriggerRef}
            component="button"
            type="button"
            onClick={handleInputClick}
            aria-label="Add tag"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "16px",
              border: "1px solid #D5DCE1",
              bgcolor: "#FFF",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              p: 0,
              cursor: "pointer",
              flexShrink: 0,
              color: "#148DFD",
              "&:hover": {
                bgcolor: "#F3FBFF",
                borderColor: "#148DFD",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 20, color: "#148DFD" }} />
          </Box>
          {isOpen && addButtonDropdownFixed && (
            <div
              ref={dropdownRef}
              style={{
                ...addButtonDropdownFixed,
                ...panelChrome,
              }}
            >
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#fff",
                  zIndex: 10,
                  marginBottom: "8px",
                }}
              >
                <TextField
                  fullWidth
                  autoFocus
                  variant="outlined"
                  placeholder="Search tags"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon className="search-icon-search" />
                      </InputAdornment>
                    ),
                    sx: {
                      "& fieldset": { border: "1px solid #D5DCE1" },
                      "& input": {
                        height: "40px",
                        fontSize: "14px",
                        fontFamily: "Inter",
                        fontWeight: 400,
                        color: "#111827 !important",
                        cursor: "text !important",
                        backgroundColor: "transparent",
                        "&::placeholder": {
                          color: "#C5CED6",
                          opacity: 1,
                          fontSize: "14px",
                          fontFamily: "Inter",
                          fontWeight: 400,
                        },
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      "&.Mui-focused fieldset": {
                        border: "1px solid #3B82F6",
                      },
                    },
                  }}
                />
              </div>
              {!loading && searchTerm && filteredTags.length === 0 && (
                <NoSearch
                  title="No tags found"
                  description="Clear your search and try again."
                  titleFontSize={14}
                  descriptionFontSize={12}
                />
              )}
              {!loading && !searchTerm && normalizedTags.length === 0 && (
                <NoSearch
                  title="No tags yet"
                  description="Tags added to this workspace will appear here."
                  titleFontSize={14}
                  descriptionFontSize={12}
                />
              )}
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(0,0,0,0.2) transparent",
                }}
              >
                {loading && (
                  <div
                    style={{
                      width: "100%",
                      textAlign: "center",
                      color: "#6B7280",
                      fontSize: "14px",
                      padding: "16px 0",
                    }}
                  >
                    Loading tags...
                  </div>
                )}
                {!loading &&
                  (searchTerm ? filteredTags : normalizedTags).map((tag) => {
                    const isSelected = returnIds
                      ? tags.includes(tag._id)
                      : tags.includes(tag.name);

                    return (
                      <Box
                        key={tag._id}
                        onClick={() => handleTagSelect(tag)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: "32px",
                          padding: "0 8px",
                          backgroundColor: isSelected ? "#F9FAFA" : "transparent",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "background-color 0.2s ease",
                          overflow: "hidden",
                          "&:hover": {
                            backgroundColor: "#F9FAFA",
                            borderRadius: "8px",
                            "& svg path": {
                              stroke: "#148DFD",
                            },
                            "& .tag-text": {
                              color: "#061522",
                            },
                          },
                          "& svg": {
                            width: "16px",
                            height: "16px",
                            marginRight: tag.name ? "8px" : "0",
                            flexShrink: 0,
                          },
                          "& svg path": {
                            stroke: isSelected ? "#148DFD" : "#556575",
                          },
                        }}
                      >
                        <ChatTag />
                        {tag.name && (
                          <Box
                            className="tag-text"
                            sx={{
                              fontSize: "14px",
                              color: isSelected ? "#061522" : "#556575",
                              fontFamily: "Inter",
                              fontWeight: 400,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {tag.name}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="TicketTagInputContainer-Main"
      style={
        singleLineEllipsis
          ? { flexGrow: 0, minWidth: 104, maxWidth: "100%", width: "100%" }
          : undefined
      }
    >
      <div className="field-wrapper">
        {showHeader && <h3 className="field-label">{headerLabel}</h3>}

        <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
          {/* Main Input Container */}
          <div
            onClick={handleInputClick}
            style={{
              borderRadius: "8px",
              background: "#FFF",
              boxShadow: "0px 1px 2px rgba(6, 21, 34, 0.05)",
              fontSize: "14px",
              height: allowHeightExpansion ? "auto" : `${sxHeight}px`,
              minHeight: `${sxHeight}px`,
              width: "100%",
              minWidth: singleLineEllipsis ? "unset" : "200px",
              maxWidth: "100%",
              border: "1px solid #D5DCE1",
              cursor: "pointer",
              position: "relative",
              display: "flex",
              alignItems:
                centerContent || (!singleLineEllipsis && !isWrapped)
                  ? "center"
                  : !singleLineEllipsis && isWrapped
                    ? "flex-start"
                    : "center",

              padding:
                centerContent || (!singleLineEllipsis && !isWrapped)
                  ? "0px 12px"
                  : !singleLineEllipsis && isWrapped
                    ? "0px 16px 4px 16px"
                    : "0px 12px",


              overflow: allowHeightExpansion ? "visible" : "hidden",
              ...formControlSx,
            }}
          >
            {/* Content */}
            {tags.length === 0 || hideInlineChips ? (
              <span
                style={{
                  color: hideInlineChips ? "#C5CED6" : "#405261",
                  fontSize: "14px",
                  fontFamily: "Inter",
                  fontWeight: 400,
                  ...labelEllipsisStyle,
                }}
              >
                {loading ? `${emptyLabel} ` : emptyLabel}
              </span>
            ) : (
              <>
                {/* When allowHeightExpansion is FALSE, show count format */}
                {!allowHeightExpansion ? (
                  <span
                    style={{
                      color: "#405261",
                      fontSize: "14px",
                      fontFamily: "Inter",
                      fontWeight: 400,
                      ...labelEllipsisStyle,
                    }}
                  >
                    ({tags.length}) tag{tags.length !== 1 ? "s" : ""} added
                  </span>
                ) : (
                  <div
                    ref={selectInputRef}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: allowHeightExpansion ? "wrap" : "nowrap",
                      flex: 1,
                      height: allowHeightExpansion ? "auto" : "100%",
                      minHeight: centerContent
                        ? `${Math.max(sxHeight - 2, 24)}px`
                        : allowHeightExpansion
                          ? "24px"
                          : "auto",
                      overflowX: allowHeightExpansion ? "visible" : "auto",
                      overflowY: allowHeightExpansion ? "visible" : "hidden",
                      gap: "4px",
                      scrollbarWidth: allowHeightExpansion ? "auto" : "none",
                      msOverflowStyle: allowHeightExpansion ? "auto" : "none",
                      lineHeight: centerContent ? "normal" : allowHeightExpansion ? "32px" : "normal",
                      alignSelf: centerContent ? "center" : undefined,
                      ...chipsContainerSx,
                    }}
                  >
                    {showTagsPrefix && (
                      <span
                        style={{
                          fontSize: 14,
                          fontFamily: "Inter",
                          fontWeight: 400,
                          color: "#405261",
                          whiteSpace: "nowrap",
                          marginRight: 8,
                          userSelect: "none",
                        }}
                      >
                        Add Tags:
                      </span>
                    )}
                    {tags.map((value) => {
                      const tagData = normalizedTags.find((t) =>
                        returnIds ? t._id === value : t.name === value
                      );
                      return (
                        <Chip
                          size="small"
                          sx={{
                            height: 24,
                            minWidth: "fit-content",
                            maxWidth: 200,
                            backgroundColor: "#F9FAFA",
                            border: "1px solid #C5CED6",
                            borderRadius: "20px",
                            flexShrink: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            lineHeight: 1,
                            "& .MuiChip-label": {
                              fontSize: 12,
                              color: "#405261",
                              fontWeight: 400,
                              pr: 0,
                              pl:'6px',
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: "16px",
                            },
                            "& .MuiChip-deleteIcon": {
                              width: 14,
                              height: 14,
                              margin: "0 4px 0 0",
                              color: "#405261",
                              flexShrink: 0,
                              mb:'2px',
                              alignSelf: "center",
                              display: "block",
                            },
                          }}
                          key={value}
                          label={tagData ? tagData.name : value}
                          deleteIcon={<Cancel />}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onDelete={(e) => {
                            e.stopPropagation();
                            handleDeleteTag(value);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}



            {/* Dropdown Arrow */}
            <div
              style={{
                marginLeft: singleLineEllipsis ? 0 : "8px",
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: centerContent ? 0 : !singleLineEllipsis && isWrapped ? "7px" : "0px",
                alignSelf: centerContent ? "center" : undefined,
                flexShrink: 0,
              }}
            >
              <Dropdown style={{ width: "16px", height: "16px" }} />
            </div>
          </div>

          {/* Dropdown Menu */}
          {isOpen && (
            <div
              ref={dropdownRef}
              style={{
                position: "absolute",
                top: dropDownTop,
                left: 0,
                right: dropdownWidth && !dropdownMatchTrigger ? "auto" : 0,
                width: dropdownMatchTrigger ? "100%" : dropdownWidth || undefined,
                zIndex: 1000,
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1)",
                display: "flex",
                padding: '8px 8px',
                flexDirection: "column",
                maxHeight: dropdownHeight,
                overflow: "hidden",
              }}
            >
              {/* Search Input */}
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#fff",
                  zIndex: 10,
                  marginBottom: "8px",
                }}
              >
                <TextField
                  fullWidth
                  autoFocus
                  variant="outlined"
                  placeholder="Search tags"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ width: 16, height: 16 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      "& fieldset": { border: "1px solid #D5DCE1" },
                      "& input": {
                        height: "40px",
                        padding: "0 8px",
                        fontSize: "14px",
                        fontFamily: "Inter",
                        fontWeight: 400,
                        color: "#111827 !important",
                        cursor: "text !important",
                        backgroundColor: "transparent",
                        "&::placeholder": {
                          color: "#C5CED6",
                          opacity: 1,
                          fontSize: "14px",
                          fontFamily: "Inter",
                          fontWeight: 400,
                        },
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      "&.Mui-focused fieldset": {
                        border: "1px solid #3B82F6",
                      },
                    },
                  }}
                />
              </div>
              {!loading && searchTerm && filteredTags.length === 0 && (
                <NoSearch
                  title="No tags found"
                  description="Clear your search and try again."
                  titleFontSize={14}
                  descriptionFontSize={12}
                />
              )}
              {!loading && !searchTerm && normalizedTags.length === 0 && (
                <NoSearch
                  title="No tags yet"
                  description="Tags added to this workspace will appear here."
                  titleFontSize={14}
                  descriptionFontSize={12}
                />
              )}
              {/* Tag List Container */}
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(0,0,0,0.2) transparent",
                }}
              >
                {/* Loading State */}
                {loading && (
                  <div
                    style={{
                      width: "100%",
                      textAlign: "center",
                      color: "#6B7280",
                      fontSize: "14px",
                      padding: "16px 0",
                    }}
                  >
                    Loading tags...
                  </div>
                )}

                {/* Tag List */}
                {!loading &&
                  (searchTerm ? filteredTags : normalizedTags).map((tag, index) => {
                    const isSelected = returnIds
                      ? tags.includes(tag._id)
                      : tags.includes(tag.name);
                    const atLimit = tags.length >= maxAllowed && !isSelected;

                    return (
                      <Box
                        key={tag._id}
                        onClick={() => {
                          if (atLimit) {
                            toast.error(
                              `You can only add up to ${maxAllowed} tags.`
                            );
                            return;
                          }
                          handleTagSelect(tag);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: "32px",
                          padding: "0 8px",
                          backgroundColor: isSelected ? "#F9FAFA" : "transparent",
                          borderRadius: "8px",
                          cursor: atLimit ? "not-allowed" : "pointer",
                          opacity: atLimit ? 0.45 : 1,
                          transition: "background-color 0.2s ease",
                          overflow: "hidden",
                          "&:hover": {
                            backgroundColor: "#F9FAFA",
                            borderRadius: "8px",

                            "& svg path": {
                              stroke: "#148DFD", // same as selected
                            },
                            "& .tag-text": {
                              color: "#061522", // same as selected
                            },
                          },
                          // Apply styles only to ChatTag svg, not DropdownSelectionTick
                          "& .chat-tag-svg": {
                            width: "16px",
                            height: "16px",
                            marginRight: tag.name ? "8px" : "0",
                            flexShrink: 0,
                          },
                          "& .chat-tag-svg path": {
                            stroke: isSelected ? "#148DFD" : "#556575",
                          },
                        }}
                      >
                        {/* Add a className to distinguish ChatTag svg */}
                        <ChatTag className="chat-tag-svg" />
                        {tag.name && (
                          <Box
                            className="tag-text"
                            sx={{
                              fontSize: "14px",
                              color: isSelected ? "#061522" : "#556575",
                              fontFamily: "Inter",
                              fontWeight: 400,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {tag.name}
                          </Box>
                        )}
                        {showSelectionTick && isSelected && <DropdownSelectionTick />}
                
                      </Box>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TicketTagInput;